import { supabase, TABLES } from "../lib/supabase";
import type { Commission } from "./commissions";
import { getCommissionsByContractType } from "./commissions";

export interface ContractType {
  id: number;
  type: string;
  company: string;
  category: string;
  description: string;
  active: boolean;
  min_duration_months?: number;
  company_details?: ContractCompany;
  commissions?: Commission[];
}

export interface ContractCompany {
  id: number;
  company: string;
  active: boolean; // Note: This matches the typo in the database schema
  color_code: string;
  color_code2: string;
}

export interface CreateContractTypeData {
  type: string;
  company: string;
  category: string;
  description: string;
  min_duration_months?: number;
  active: boolean;
}

export type ContractTypeStatusFilter = "all" | "active" | "inactive";

export interface ContractTypesPageParams {
  page: number;
  pageSize: number;
  searchTerm?: string;
  companies?: string[];
  categories?: string[];
  status?: ContractTypeStatusFilter;
  category?: string;
}

export interface PaginatedContractTypesResult {
  data: ContractType[];
  total: number;
}

const DEFAULT_BATCH_SIZE = 1000;

const escapeLikeTerm = (value: string): string =>
  value.replace(/[%_]/g, "\\$&").replace(/,/g, "\\,");

function buildContractTypesQuery({
  category,
  categories,
  companies,
  searchTerm,
  status = "active",
}: {
  category?: string;
  categories?: string[];
  companies?: string[];
  searchTerm?: string;
  status?: ContractTypeStatusFilter;
}) {
  let query = supabase
    .from(TABLES.CONTRACT_TYPES)
    .select("*", { count: "exact" });

  if (status === "active") {
    query = query.eq("active", true);
  } else if (status === "inactive") {
    query = query.eq("active", false);
  }

  if (category && category !== "all") {
    query = query.eq("category", category);
  }

  if (categories && categories.length > 0) {
    query = query.in("category", categories);
  }

  if (companies && companies.length > 0) {
    query = query.in("company", companies);
  }

  if (searchTerm && searchTerm.trim()) {
    const escapedTerm = escapeLikeTerm(searchTerm.trim());
    query = query.or(
      `type.ilike.%${escapedTerm}%,company.ilike.%${escapedTerm}%,category.ilike.%${escapedTerm}%,description.ilike.%${escapedTerm}%`
    );
  }

  return query
    .order("type", { ascending: true })
    .order("id", { ascending: true });
}

async function getAllContractTypesInBatches({
  category,
  status,
}: {
  category?: string;
  status?: ContractTypeStatusFilter;
}): Promise<ContractType[]> {
  const allRows: ContractType[] = [];
  let from = 0;
  let hasMoreRows = true;

  while (hasMoreRows) {
    const to = from + DEFAULT_BATCH_SIZE - 1;
    const { data, error } = await buildContractTypesQuery({
      category,
      status,
    }).range(from, to);

    if (error) {
      console.error("Error fetching contract types batch:", error);
      return [];
    }

    const rows = data || [];
    allRows.push(...rows);

    if (rows.length < DEFAULT_BATCH_SIZE) {
      hasMoreRows = false;
    } else {
      from += DEFAULT_BATCH_SIZE;
    }
  }

  return allRows;
}

export async function createContractType(
  data: CreateContractTypeData
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.from(TABLES.CONTRACT_TYPES).insert([data]);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Error creating contract type:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

export async function getContractTypesByCategory(
  category: string,
  limitItems: boolean = false
): Promise<ContractType[]> {
  if (limitItems) {
    const { data } = await getContractTypesPage({
      page: 1,
      pageSize: 20,
      category,
      status: "active",
    });
    return data;
  }

  return getAllContractTypesInBatches({ category, status: "active" });
}

export async function getContractTypesPage(
  params: ContractTypesPageParams
): Promise<PaginatedContractTypesResult> {
  const page = Math.max(1, params.page);
  const pageSize = Math.max(1, params.pageSize);
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await buildContractTypesQuery({
    category: params.category,
    categories: params.categories,
    companies: params.companies,
    searchTerm: params.searchTerm,
    status: params.status ?? "active",
  }).range(from, to);

  if (error) {
    console.error("Error fetching paginated contract types:", error);
    return { data: [], total: 0 };
  }

  return {
    data: data || [],
    total: count ?? 0,
  };
}

export async function getContractTypes(): Promise<ContractType[]> {
  return getAllContractTypesInBatches({ category: "all", status: "active" });
}

export async function getDetailsForContractTypes(
  contractTypes: ContractType[]
): Promise<(ContractType & { company_details: ContractCompany })[]> {
  const contractTypesWithDetails = await Promise.all(
    contractTypes.map(async (contractType) => {
      // Fetch company details
      const { data: companyData } = await supabase
        .from(TABLES.CONTRACT_COMPANIES)
        .select("id, company, active, color_code, color_code2")
        .eq("company", contractType.company)
        .eq("active", true)
        .single();

      // Fetch commissions
      const commissions = await getCommissionsByContractType(contractType.id);

      return {
        ...contractType,
        company_details: companyData || {
          id: 0,
          company: contractType.company,
          active: true,
          color_code: "#000000",
          color_code2: "#000000",
        },
        commissions: commissions || [],
      } as ContractType & { company_details: ContractCompany };
    })
  );
  return contractTypesWithDetails;
}

export async function updateContractType(
  id: number,
  data: Partial<ContractType>
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from(TABLES.CONTRACT_TYPES)
      .update(data)
      .eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Error updating contract type:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

export async function deleteContractType(
  id: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from(TABLES.CONTRACT_TYPES)
      .delete()
      .eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Error deleting contract type:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}
